import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function ImageCarousel({ images = [], alt = "", className = "", maxHeight = "max-h-80" }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const validImages = images.filter(Boolean);
  const total = validImages.length;

  const next = useCallback(() => {
    if (total > 1) setCurrent((c) => (c + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    if (total > 1) setCurrent((c) => (c - 1 + total) % total);
  }, [total]);

  // Auto-play every 3.5s
  useEffect(() => {
    if (total <= 1 || paused) return;
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, [total, paused, next]);

  if (total === 0) return null;
  if (total === 1) {
    return <img src={validImages[0]} alt={alt} className={`w-full ${maxHeight} object-contain bg-slate-50 ${className}`} />;
  }

  return (
    <div
      className={`relative group ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="image-carousel"
    >
      {/* Image */}
      <img
        src={validImages[current]}
        alt={`${alt} (${current + 1}/${total})`}
        className={`w-full ${maxHeight} object-contain bg-slate-50 transition-opacity duration-300`}
      />

      {/* Arrows */}
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
        data-testid="carousel-prev"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
        data-testid="carousel-next"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
        {validImages.map((_, i) => (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
            className={`w-2 h-2 rounded-full transition-colors ${i === current ? "bg-white" : "bg-white/40"}`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
